import { TableCellProps } from "@mui/material";
import * as React from "react";
import { IS_DEV } from "@/constants";
import {
  ColumnsAreHideableContext,
  useIsColumnVisible,
} from "./columnVisibility";
import { StyledTableCell } from "./CustomTable";

/**
 * A body cell that knows which column it belongs to, and renders nothing when
 * that column isn't being shown.
 *
 * `columnId` is optional. Every list has to be converted — a line clamp needs an
 * element to land on, and the only one it gets is this wrapper, so a row still
 * on `StyledTableCell` would wrap with nothing bounding its height — but only
 * the lists that get a column picker need their columns to be nameable. An
 * unnamed cell always renders.
 *
 * Needed for any per-user column choice (a column picker, a density preset that
 * drops columns). The head and the cells are matched by position — widths,
 * truncation and the pinned action column are all `nth-of-type` rules — so a
 * header may only be dropped if its `<td>` goes with it. `CustomTable` decides
 * once and publishes the surviving ids; this reads them.
 *
 * A row opts in cell by cell. Rows that keep using `StyledTableCell` are
 * unaffected and simply can't have their columns hidden, which is the right
 * default until each list is converted.
 */
/** Once per page load — the message names no table, so repeating it adds nothing. */
let warnedAboutUnnamedCell = false;

export default function Cell({
  columnId,
  children,
  ...props
}: { columnId?: string } & TableCellProps) {
  const visible = useIsColumnVisible(columnId);
  const hideable = React.useContext(ColumnsAreHideableContext);
  // Fires while everything is still visible, which is the point: unnamed here
  // means this cell keeps rendering after its header is hidden, and the symptom
  // is the whole table dropping to auto layout with the action buttons scrolled
  // off — long after whoever added the cell has moved on.
  //
  // Said once. This runs per cell, per row, per render: three unnamed cells on a
  // 25-row table is 75 identical lines a render, multiplied again by every sort
  // click and StrictMode's double-invoke. That buries the `sync()` warning in
  // CustomTable — which dedupes, and is the one that names the broken table.
  if (IS_DEV && hideable && columnId === undefined && !warnedAboutUnnamedCell) {
    warnedAboutUnnamedCell = true;
    console.warn(
      "[CustomTable] a <Cell /> on a table whose columns can be hidden has no `columnId`. " +
        "It will keep rendering its <td> after its header is dropped, which puts every " +
        "column-position rule one column out."
    );
  }
  if (!visible) return null;
  return (
    <StyledTableCell align="left" {...props}>
      {/* Contents always sit in a wrapper so a line clamp has something to land
          on. `-webkit-line-clamp` needs `display: -webkit-box`, and setting
          `display` on a <td> drops it out of the table layout altogether —
          every cell in the row stacks into the first column.

          Carries no inline style: an inline `display` beats the class rule that
          has to switch it to `-webkit-box`, which silently made the clamp inert.
          Everything it needs comes from `layoutSx`, which targets it as the
          cell's `*:only-child`. */}
      <span data-cell-content="">{children}</span>
    </StyledTableCell>
  );
}
